if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          mediaGallery: "[id^=Media-Gallery]",
          liveRegion: '[id^="GalleryStatus"]',
          viewer: '[id^="GalleryViewer"]',
          thumbnailsWrapper: ".f-product__media-thumbnails-wrapper",
          thumbnails: '[id^="GalleryThumbnails"]',
          thumbnailItems: ['[id^="GalleryThumbnailItem"]'],
          videos: [".f-video__embed"],
          models: ["product-model"],
          medias: [".f-product__media"],
          xrButton: "[data-first-xr-button]",
          sliderCounter: ".flickity-counter--current",
          toggleZoom: [".js-photoswipe--zoom"],
        };

        let touchingCarousel = false;
        let startXCoordinate;

        document.body.addEventListener(
          "touchstart",
          (e) => {
            if (e.target.closest(".flickity-slider")) {
              touchingCarousel = true;
            } else {
              touchingCarousel = false;
              return;
            }
            startXCoordinate = e.touches[0].pageX;
          },
          { passive: true }
        );

        document.body.addEventListener(
          "touchmove",
          (e) => {
            if (touchingCarousel && e.cancelable) {
              if (Math.abs(e.touches[0].pageX - startXCoordinate) > 10) {
                e.preventDefault();
              }
            }
          },
          { passive: false }
        );
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        clearInterval(this.check);
        if (this._variantChangeUnsubscriber) this._variantChangeUnsubscriber();
        if (this._optionSelectionUnsubscriber) this._optionSelectionUnsubscriber();
      }

      getSelectedVariant() {
        const selectedVariant = this.querySelector(
          "[data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      init() {
        this.domNodes = queryDomNodes(this.selectors, this);
        // Inject CSS for smooth media fade transitions if not already present
        try {
          if (!document.getElementById('product-media-fade-style')) {
            const st = document.createElement('style');
            st.id = 'product-media-fade-style';
            st.textContent = "\n.f-media-fade{transition:opacity 240ms ease-in-out;}\n.f-media-fade.fading{opacity:0;pointer-events:none;}\n";
            document.head.appendChild(st);
          }
        } catch (e) {
          // ignore style injection failures
        }
        // Configurable fallback window (ms) for correlating selection -> variantChange
        // When selection tokens are not available, we fall back to this time window.
        // Set data-option-selection-window-ms="0" to disable the fallback.
        this.optionSelectionWindowMs = Number(this.dataset.optionSelectionWindowMs);
        if (isNaN(this.optionSelectionWindowMs)) this.optionSelectionWindowMs = 800;
        this.mediaLayout = this.domNodes.mediaGallery.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === "true";
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest("product-info");
        this.enableZoom = this.dataset.enableZoom === "true";
        this.enableVariantGroupImages =
          this.dataset.enableVariantGroupImages === "true";
        // Enable better auto mapping fallback when VariantGroupImages absent
        // Can be controlled via data-enable-auto-variant-mapping on the element
        this.enableAutoVariantMapping =
          this.dataset.enableAutoVariantMapping === undefined
            ? true
            : this.dataset.enableAutoVariantMapping === "true";
        this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true";
        this.variantPickers = this.section.querySelector(
          "[data-variant-picker]"
        );
        this.disableDefaultVariant =
          this.variantPickers &&
          this.variantPickers.dataset.disableDefaultVariant === "true";
        this.variantData = this.getVariantData();
        // selected variant can be provided as an input value (id string) or as
        // a JSON object injected by Liquid in a <script data-selected-variant>.
        const selectedVariantRaw =
          (this.section &&
            this.section.querySelector('input[name="id"]')?.value) ||
          this.getSelectedVariant();

        let selectedVariantId = null;
        if (selectedVariantRaw && typeof selectedVariantRaw === 'object' && selectedVariantRaw.id) {
          selectedVariantId = Number(selectedVariantRaw.id);
        } else {
          selectedVariantId = Number(selectedVariantRaw);
        }

        this.currentVariant = this.variantData.find(
          (variant) => variant.id === selectedVariantId
        );

        // Subscribe to global variant change events so media can update when
        // the selected variant changes elsewhere on the page (product-info).
        try {
          // Listen to option selection events to capture the selection token
          // and whether the selection was for a size field. The token will
          // be forwarded by product-info when the resulting variantChange
          // is emitted allowing a reliable correlation between selection
          // and variantChange.
          this._optionSelectionUnsubscriber = window.FoxThemeEvents.subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            (evt) => {
              try {
                const data = evt?.data || evt?.detail || {};
                const token = data.selectionToken || null;
                const isSize = !!data.isSize;
                this._lastOptionSelection = { token, isSize, time: Date.now() };
              } catch (e) {
                // swallow
              }
            }
          );

          this._variantChangeUnsubscriber = window.FoxThemeEvents.subscribe(
            PUB_SUB_EVENTS.variantChange,
            (evt) => {
              const data = evt?.data || evt?.detail || {};
              const variant = data.variant;
              if (!variant) return;
              try {
                const incomingToken = data.selectionToken || null;
                const last = this._lastOptionSelection;

                // 1) Prefer token-based correlation when available
                if (incomingToken && last && last.token && String(incomingToken) === String(last.token)) {
                  if (last.isSize) {
                    // update internal variant but skip image refresh
                    this.currentVariant = variant;
                    this._lastOptionSelection = null;
                    return;
                  }
                }

                // 2) Fallback: time-window based heuristic (configurable via data-option-selection-window-ms)
                try {
                  if (!incomingToken && last && last.isSize && this.optionSelectionWindowMs > 0) {
                    const elapsed = Date.now() - last.time;
                    if (elapsed < this.optionSelectionWindowMs) {
                      this.currentVariant = variant;
                      this._lastOptionSelection = null;
                      return;
                    }
                  }
                } catch (e) {
                  // ignore and continue to update
                }
              } catch (e) {
                // fallthrough to normal update
              }
              this.updateVariantMedia(variant);
            }
          );
        } catch (e) {
          console.warn("product-media: could not subscribe to variantChange/optionValueSelectionChange", e);
        }

        if (!this.enableVariantGroupImages || this.onlyImage) {
          setTimeout(() => {
            this.removeAttribute("data-media-loading");
          }, 100);
        }
        if (this.mediaLayout === "carousel") {
          setTimeout(() => {
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
          });
          this.initGallerySlider();
        } else {
          const initSliders = () => {
            this.domNodes.mediaGallery &&
              this.domNodes.mediaGallery.initSlider();
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
            this.mediaGallery = this.domNodes.mediaGallery.slider.instance;
            this.thumbnails =
              this.domNodes.thumbnails &&
              this.domNodes.thumbnails.slider.instance;
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          };
          if (FoxThemeSettings.isMobile) {
            initSliders();
          }
          document.addEventListener("matchMobile", initSliders);
          document.addEventListener("unmatchMobile", () => {
            this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
            this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          });
          if (this.enableZoom) this.initImageZoom();
        }
        if (this.onlyImage && this.enableZoom) this.initImageZoom();

        setTimeout(() => {
          this.dispatchEvent(
            new CustomEvent("product-media:loaded", {
              bubbles: true,
              detail: {
                currentVariant: this.currentVariant,
              },
            })
          );
          // Ensure initial variant media mapping is applied on first load
          try {
            // run after a short delay so any other on-load wiring completes
            setTimeout(() => {
              if (this.currentVariant) {
                // Only attempt to filter if VariantGroupImages enabled or auto mapping allowed
                if (this.enableVariantGroupImages || this.enableAutoVariantMapping) {
                  this.updateVariantMedia(this.currentVariant);
                }
              }
            }, 120);
          } catch (e) {
            console.warn('product-media: initial updateVariantMedia failed', e);
          }
        }, 100);
      }

      update() {
        if (this.domNodes.mediaGallery) this.domNodes.mediaGallery.update();
        if (this.domNodes.thumbnails) this.domNodes.thumbnails.update();
      }

      initGallerySlider() {
        if (!this.domNodes.medias.length) return;
        this.selectedSlide = null;
        this.selectedMediaType = "";
        this.check = setInterval(() => {
          this.mediaGallery =
            this.domNodes.mediaGallery.slider &&
            this.domNodes.mediaGallery.slider.instance;
          this.thumbnails =
            this.domNodes.thumbnails &&
            this.domNodes.thumbnails.slider &&
            this.domNodes.thumbnails.slider.instance;

          if (this.mediaGallery && typeof this.mediaGallery == "object") {
            clearInterval(this.check);
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));

            if (this.thumbnails) {
              if (this.thumbnails.cells && this.thumbnails.cells[0]) {
                this.thumbnails.select(0);
                this.handleScreenChange();
              }
            }
            if (this.enableZoom) this.initImageZoom();
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          }
        }, 100);
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.querySelector('[type="application/json"]').textContent
          );
        return this.variantData;
      }

      // Build a mapping from variant id -> array of media ids by attempting to
      // match the color option value against the media filename only.
      // NOTE: we intentionally do NOT use image alt text — only the filename
      // (decoded and normalized) is used for matching. This reduces false
      // positives caused by descriptive alt text or other attributes.
      // This is a best-effort fallback when VariantGroupImages JSON is not provided.
      buildVariantMediaMapping() {
        try {
          // debug logging removed in cleanup
          if (this._autoVariantMapping) return this._autoVariantMapping;
          const variants = this.getVariantData() || [];
          const medias = Array.from(this.querySelectorAll('.f-product__media')) || [];

          // Detect which option field corresponds to 'color' so auto-mapping
          // will only use the color option value (ignore size options).
          let colorOptionIndex = null; // 1-based index
          try {
            const vp = this.variantPickers || (this.section && this.section.querySelector('[data-variant-picker]'));
            if (vp) {
              const fields = vp.querySelectorAll('.variant-picker__field');
              if (fields && fields.length) {
                fields.forEach((f, idx) => {
                  try {
                    const name = (f.dataset && f.dataset.optionName) ? String(f.dataset.optionName).toLowerCase() : '';
                    const inner = f.querySelector && (f.querySelector('.variant-picker__input--color') || f.querySelector('.variant-picker__color'));
                    if (!colorOptionIndex) {
                      if (name && (name.indexOf('color') !== -1 || name.indexOf('colour') !== -1 || name.indexOf('颜色') !== -1)) {
                        colorOptionIndex = idx + 1;
                      } else if (inner) {
                        // presence of color-related inner class indicates this is color field
                        colorOptionIndex = idx + 1;
                      }
                    }
                  } catch (e) {
                    // ignore
                  }
                });
              }
            }
          } catch (e) {
            // ignore detection errors and fall back to using all options
            colorOptionIndex = null;
          }

          // Build searchable tokens for each media: filename tokens, src tokens
          const mediaSearch = medias.map((m) => {
            const img = m.querySelector('img');
            const src = img ? (img.currentSrc || img.src || '') : '';
            // clean src: remove querystring/hash and decodeURIComponent to avoid matching issues
            let cleanedSrc = '';
            try {
              cleanedSrc = String(src || '').split('?')[0].split('#')[0];
              try {
                cleanedSrc = decodeURIComponent(cleanedSrc);
              } catch (e) {
                // ignore decode errors
              }
            } catch (e) {
              cleanedSrc = '';
            }
            // extract filename from cleanedSrc
            let filename = '';
            try {
              const parts = cleanedSrc.split('/');
              filename = parts.length ? parts[parts.length - 1] : '';
            } catch (e) {
              filename = '';
            }
            // normalize filename by replacing separators with spaces, remove extension
            const filenameNoExt = filename.replace(/\.[^.]+$/, '');
            const filenameNormalized = filenameNoExt.replace(/[-_\.]+/g, ' ').toLowerCase();
            return {
              mediaId: String(m.dataset.mediaId),
              node: m,
              filename: filename.toLowerCase(),
              filenameNormalized
            };
          });

          const mapping = variants.map((v) => {
            const optionValues = [];
            if (colorOptionIndex) {
              const val = v[`option${colorOptionIndex}`];
              if (val) optionValues.push(String(val).toLowerCase());
            } else {
              // fallback: use all option values (legacy behavior)
              for (let i = 1; i <= 3; i++) {
                const val = v[`option${i}`];
                if (val) optionValues.push(String(val).toLowerCase());
              }
            }
            // For each media, test stronger matches using only the normalized
            // filename. This avoids accidental matches from alt text or full src.
            const matched = mediaSearch.filter((ms) => {
              return optionValues.some((ov) => {
                if (!ov) return false;
                // normalized filename contains phrase (handles kebab_case, snake_case, dots)
                return !!(ms.filenameNormalized && ms.filenameNormalized.indexOf(ov) !== -1);
              });
            }).map((ms) => ms.mediaId);

            return { id: v.id, media: matched };
          });

          this._autoVariantMapping = { enable: !!this.enableAutoVariantMapping, mapping };
          // mapping built
          return this._autoVariantMapping;
        } catch (e) {
          console.warn('product-media: buildVariantMediaMapping failed', e);
          console.debug('product-media: buildVariantMediaMapping error', e && e.stack);
          this._autoVariantMapping = { enable: false, mapping: [] };
          return this._autoVariantMapping;
        }
      }

      initImageZoom() {
        let dataSource = [];
        // Build lightbox datasource from the currently filtered media (if available)
        let allMedia = [];
        try {
          if (this._lastVariantMedia && this._lastVariantMedia.length && this.domNodes && this.domNodes.medias && this.domNodes.medias.length) {
            allMedia = this.filteredMediaForSelectedVariant(Array.from(this.domNodes.medias || []), this._lastVariantMedia);
          } else {
            allMedia = Array.from(this.querySelectorAll('.f-product__media'));
          }
        } catch (e) {
          allMedia = Array.from(this.querySelectorAll('.f-product__media'));
        }
        if (allMedia && allMedia.length) {
          allMedia.forEach((media) => {
            if (media.dataset.mediaType === "image") {
              dataSource.push({
                id: media.dataset.mediaIndex,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                mediaType: media.dataset.mediaType,
              });
            }
            if (media.dataset.mediaType === "model") {
              const html = `<div class="pswp__item--${media.dataset.mediaType
                }">${media.querySelector("product-model").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }

            if (
              media.dataset.mediaType === "video" ||
              media.dataset.mediaType == "external_video"
            ) {
              const html = `<div class="pswp__item--${media.dataset.mediaType
                }">${media.querySelector("deferred-media").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }
          });
        }

        const options = {
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        };

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, data, index) => {
          const el = this.querySelector(
            '[data-media-index="' + data.id + '"] img'
          );
          if (el) {
            return el;
          }
          return thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, slide) => {
          const el = this.querySelector(
            '[data-media-index="' + slide.data.id + '"] img'
          );
          if (el) {
            return el.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on("change", () => {
          const currIndex = this.lightbox.pswp.currIndex;
          if (!this.onlyImage) {
            const slider =
              this.domNodes.mediaGallery.slider &&
              this.domNodes.mediaGallery.slider.instance;
            if (slider) {
              setTimeout(() => {
                slider.select(currIndex, false, true);
              }, 300);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          const currSlide = this.lightbox.pswp.currSlide.data;
          if (
            currSlide.mediaType === "video" ||
            currSlide.mediaType === "model" ||
            currSlide.mediaType === "external_video"
          ) {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }

          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium  f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        addEventDelegate({
          selector: this.selectors.toggleZoom[0],
          context: this,
          handler: (e, media) => {
            const isImage = media.dataset.mediaType === "image";
            if (isImage) {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      onSlideChanged(index) {
        this.draggable = true;
        this.selectedSlide = this.mediaGallery.selectedElement;
        this.selectedMediaType = this.selectedSlide.dataset.mediaType;
        this.selectedMediaId = this.selectedSlide.dataset.mediaId;

        const mediaAspectRatio = this.selectedSlide.dataset.aspectRatio;
        this.domNodes.viewer.style.setProperty(
          "--media-aspect-ratio",
          mediaAspectRatio
        );

        if (
          "model,external_video,video".includes(this.selectedMediaType) &&
          this.draggable
        ) {
          this.toggleDraggable();
        } else {
          this.toggleDraggable(true);
        }

        if (this.selectedMediaType === "model") {
          this.domNodes.xrButton &&
            this.domNodes.xrButton.setAttribute(
              "data-shopify-model3d-id",
              this.selectedMediaId
            );
        }

        if (this.thumbnails) {
          this.thumbnails.select(index);
        }

        if (this.domNodes.sliderCounter) {
          this.domNodes.sliderCounter.textContent = index + 1;
        }

        this.domNodes.mediaGallery.classList.remove("pointer-move");

        this.playActiveMedia(this.selectedSlide);
      }

      handleScreenChange() {
        document.addEventListener("matchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length > 4) {
            this.thumbnails.options.wrapAround = true;
            this.domNodes.thumbnails.update();
          }
        });
        document.addEventListener("unmatchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length < 6) {
            this.thumbnails.options.wrapAround = false;
            this.domNodes.thumbnails.update();
          }
        });
        if (this.thumbnails.cells.length > 5) {
          this.domNodes.thumbnails.update();
          this.thumbnails.options.wrapAround = true;
          this.domNodes.thumbnails.update();
        }
      }

      toggleDraggable(status = false) {
        this.draggable = status;
        this.mediaGallery.options.draggable = status;
        this.mediaGallery.updateDraggable();
      }
      playActiveMedia(selected) {
        window.pauseAllMedia();
        const deferredMedia = selected.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      filteredMediaForSelectedVariant(items, currentVariantMedia) {
        let index = 0;
        let prioritizedResults = [];
        let otherResults = [];
        items.forEach((item) => {
          const dataIdMedia = String(item.dataset.mediaId);
          if (currentVariantMedia && currentVariantMedia.length > 0) {
            if (currentVariantMedia.includes(dataIdMedia)) {
              item.dataset.index = index++;
              prioritizedResults.push(item);
            }

            if (item.dataset.mediaType !== "image") {
              item.dataset.index = index++;
              otherResults.push(item);
            }
          } else {
            item.dataset.index = index++;
            prioritizedResults.push(item);
          }
        });

        const sortByVariantOrder = (a, b) => {
          const indexA = currentVariantMedia.indexOf(String(a.dataset.mediaId));
          const indexB = currentVariantMedia.indexOf(String(b.dataset.mediaId));
          return indexA - indexB;
        };

        prioritizedResults.sort(sortByVariantOrder);

        const results = [...prioritizedResults, ...otherResults];

        return results;
      }

      setActiveMedia(mediaId) {
        const selectedMedia = this.domNodes.mediaGallery.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!selectedMedia) return;
        const mediaIndex = Number(selectedMedia.dataset.mediaIndex);

        if (this.mediaLayout === "carousel" || FoxThemeSettings.isMobile) {
          this.mediaGallery &&
            this.mediaGallery.select(mediaIndex, false, true);
        } else {
          this.scrollIntoView(selectedMedia);
        }
      }

      // Update the visible media to the ones that correspond to the provided variant.
      // Uses existing filteredMediaForSelectedVariant helper to compute which slides to
      // show. Minimal manipulation: remove existing slides and append filtered ones.
      updateVariantMedia(variant) {
        // updateVariantMedia invoked
        // If variant group images are disabled, we used to just hide non-featured media
        // and return early. However, if auto-variant-mapping is enabled, allow the
        // mapping logic to run so we can match by filename/alt even when the theme
        // setting for VariantGroupImages is false.
        if (!this.enableVariantGroupImages && !this.enableAutoVariantMapping) {
          // fallback: show only featured media
          if (variant && variant.featured_media) {
            const featuredId = String(variant.featured_media.id);
            try {
              const medias = this.domNodes.medias || [];
              medias.forEach((m) => {
                if (String(m.dataset.mediaId) === featuredId) {
                  m.classList.remove('f-hidden');
                } else {
                  m.classList.add('f-hidden');
                }
              });

              const thumbs = this.domNodes.thumbnailItems || [];
              thumbs.forEach((t) => {
                if (String(t.dataset.mediaId) === featuredId) {
                  t.classList.remove('f-hidden');
                } else {
                  t.classList.add('f-hidden');
                }
              });

              // Ensure the gallery selects the featured media
              this.setActiveMedia(featuredId);

              // Try refreshing Flickity instances so hidden slides are accounted for visually
              try {
                const mainFlickity = window.FoxTheme.Flickity.data(this.domNodes.mediaGallery);
                if (mainFlickity && mainFlickity.reloadCells) mainFlickity.reloadCells();
                const thumbsFlickity = window.FoxTheme.Flickity.data(this.domNodes.thumbnails);
                if (thumbsFlickity) {
                  thumbsFlickity.reloadCells && thumbsFlickity.reloadCells();
                  thumbsFlickity.reposition && thumbsFlickity.reposition();
                }
              } catch (e) {
                console.warn('product-media: flickity refresh failed', e);
              }
            } catch (e) {
              console.warn('product-media: hide non-featured failed', e);
            }
          }
          return;
        }

        const initalMediaIds = (this.domNodes.medias || []).map((item) => String(item.dataset.mediaId));

        // Determine currentVariantMedia mapping by searching VariantGroupImages JSON if present
        let currentVariantMedia = null;
        try {
          const variantGroupJson =
            (this.section && this.section.querySelector('#VariantGroupImages[type="application/json"]')) ||
            document.getElementById('VariantGroupImages');
          if (variantGroupJson) {
            const mapping = JSON.parse(variantGroupJson.textContent).mapping || [];
            const group = mapping.find((item) => Number(item.id) === Number(variant.id));
            currentVariantMedia = group ? (group.media || []).map(String) : null;
          }
        } catch (e) {
          console.warn('product-media: failed to parse VariantGroupImages', e);
        }

        if (!currentVariantMedia || !currentVariantMedia.length) {
          // Attempt to auto build a mapping from media alt/src if VariantGroupImages absent
          try {
            const auto = this.buildVariantMediaMapping();
            if (auto && auto.enable) {
              const group = auto.mapping.find((m) => Number(m.id) === Number(variant.id));
              currentVariantMedia = group ? (group.media || []).map(String) : [];
            }
          } catch (e) {
            console.warn('product-media: auto mapping failed', e);
          }

          if (!currentVariantMedia || !currentVariantMedia.length) {
            currentVariantMedia = initalMediaIds.slice();
          }
        }

        // Cache the computed media IDs for the selected variant so other methods
        // (like initImageZoom) can build the lightbox from the filtered set.
        this._lastVariantMedia = Array.isArray(currentVariantMedia) ? currentVariantMedia.slice() : (currentVariantMedia || []);

        const mainMedia = this.domNodes.mediaGallery;
        const thumbMedia = this.domNodes.thumbnails;
        const isCarousel = this.mediaLayout === 'carousel' || FoxThemeSettings.isMobile;

        // Helper to perform DOM update with a fade transition on the provided container.
        const fadeMs = 240;
        const fadeUpdate = (container, updateFn) => {
          if (!container) {
            // no container: run immediately
            try { updateFn(); } catch (e) { console.error(e); }
            return;
          }
          try {
            container.classList.add('f-media-fade');
            // force reflow
            // eslint-disable-next-line no-unused-expressions
            container.offsetHeight;
            container.classList.add('fading');
            setTimeout(() => {
              try {
                updateFn();
              } catch (e) {
                console.error('product-media: fade update failed', e);
              }
              // Ensure a tiny delay to allow DOM to settle before fading back in
              requestAnimationFrame(() => requestAnimationFrame(() => {
                container.classList.remove('fading');
              }));
            }, fadeMs);
          } catch (e) {
            try { updateFn(); } catch (err) { console.error(err); }
          }
        };

        if (isCarousel) {
          try {
            const mainFlickity = window.FoxTheme.Flickity.data(mainMedia);
            if (!mainFlickity) {
              console.warn('product-media: Flickity main instance not found');
              return;
            }

            // Perform Flickity update inside a fade so the change is less jarring
            fadeUpdate(mainMedia, () => {
              let mediaToRemove = mainMedia.querySelectorAll('.f-product__media');
              mediaToRemove && mediaToRemove.forEach((slide) => mainFlickity.remove(slide));

              let mediaToShow = this.filteredMediaForSelectedVariant(this.domNodes.medias || [], currentVariantMedia);
              // appending slides
              mediaToShow.forEach((slide, index) => {
                const zoomButton = slide.querySelector('.js-photoswipe--zoom');
                if (zoomButton) zoomButton.setAttribute('data-media-index', index);
                slide.setAttribute('data-media-index', index);
                slide.setAttribute('data-index', index);
                mainFlickity.append(slide);
              });
            });


            if (thumbMedia && this.domNodes.thumbnailItems) {
              const thumbnailsFlickity = window.FoxTheme.Flickity.data(thumbMedia);
              if (thumbnailsFlickity) {
                // Update thumbnails also inside a fade to keep UI smooth
                fadeUpdate(thumbMedia, () => {
                  let thumbsToRemove = thumbMedia.querySelectorAll('.f-product__media-thumbnails-item');
                  thumbsToRemove && thumbsToRemove.forEach((slide) => thumbnailsFlickity.remove(slide));

                  let thumbsToShow = this.filteredMediaForSelectedVariant(this.domNodes.thumbnailItems || [], currentVariantMedia);
                  // appending thumbnail slides
                  thumbsToShow.forEach((slide, index) => {
                    slide.setAttribute('data-thumbnail-index', index);
                    slide.setAttribute('data-index', index);
                    thumbnailsFlickity.append(slide);
                  });

                  thumbMedia.classList.remove('disable-transition', 'md:disable-transition');
                  if (thumbsToShow.length < 5) thumbMedia.classList.add('disable-transition');
                  if (thumbsToShow.length < 6) thumbMedia.classList.add('md:disable-transition');
                  thumbnailsFlickity.option({ wrapAround: thumbsToShow.length > 5 });
                  thumbnailsFlickity.reloadCells();
                  thumbnailsFlickity.reposition();
                });
              }
            }

            mainFlickity.select(0, false, false);
          } catch (err) {
            console.error('product-media: error updating carousel media', err);
          }
          // Modal sync is handled after the main update (shared path)
        } else {
          try {
            let mediaToShow = this.filteredMediaForSelectedVariant(this.domNodes.medias || [], currentVariantMedia);
            // non-carousel mediaToShow prepared
            const mainMediaContainer = this.querySelector('.f-product__media-list');
            if (!mainMediaContainer) {
              console.warn('product-media: main media container not found');
              return;
            }

            // Use fadeUpdate to reduce flicker when swapping non-carousel DOM
            fadeUpdate(mainMediaContainer, () => {
              mainMediaContainer.innerHTML = '';
              mediaToShow.forEach((item, index) => {
                const zoomButton = item.querySelector('.js-photoswipe--zoom');
                if (zoomButton) zoomButton.setAttribute('data-media-index', index);
                item.setAttribute('data-media-index', index);
                item.setAttribute('data-index', index);
                mainMediaContainer.append(item);
              });
            });
          } catch (err) {
            console.error('product-media: error updating non-carousel media', err);
          }
        }

        // Ensure modal/fullscreen gallery is also updated for non-carousel layouts
        try {
          const modalSelector = '[id^="Modal-Media-Gallery"]';
          const modalEl = this.querySelector(modalSelector) || document.querySelector(modalSelector);
          if (modalEl) {
            const modalFlickity = window.FoxTheme.Flickity.data(modalEl);
            const modalItems = this.filteredMediaForSelectedVariant(this.domNodes.medias || [], currentVariantMedia);
            if (modalFlickity) {
              const existing = modalEl.querySelectorAll('.f-product__media');
              existing && existing.forEach((el) => modalFlickity.remove(el));
              modalItems.forEach((slide, index) => {
                const clone = slide.cloneNode(true);
                clone.setAttribute('data-media-index', index);
                const zoomBtn = clone.querySelector('.js-photoswipe--zoom');
                if (zoomBtn) zoomBtn.setAttribute('data-media-index', index);
                modalFlickity.append(clone);
              });
              modalFlickity.select(0);
              modalFlickity.reloadCells && modalFlickity.reloadCells();
              modalFlickity.reposition && modalFlickity.reposition();
            } else {
              try {
                modalEl.innerHTML = '';
                modalItems.forEach((slide, index) => {
                  const clone = slide.cloneNode(true);
                  clone.setAttribute('data-media-index', index);
                  const zoomBtn = clone.querySelector('.js-photoswipe--zoom');
                  if (zoomBtn) zoomBtn.setAttribute('data-media-index', index);
                  modalEl.append(clone);
                });
              } catch (e) {
                // swallow
              }
            }
          }
        } catch (e) {
          console.warn('product-media: modal sync failed', e);
        }

        if (this.enableZoom) {
          try {
            this.lightbox && this.lightbox.destroy();
            this.initImageZoom();
          } catch (err) {
            console.warn('product-media: error reinit lightbox', err);
          }
        }
      }

      scrollIntoView(selectedMedia) {
        if (FoxThemeSettings.isMobile) return;
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}
